import React, { useState } from 'react';
import { Play, Clock, Calendar, MapPin, Users, ExternalLink, BookOpen } from 'lucide-react';
import { siteData } from '../data/mock';
import StudyGuide from '../components/StudyGuide';

const MinistryPage = () => {
  const { ministry } = siteData;
  const [activeTab, setActiveTab] = useState('messages');
  const [showStudyGuide, setShowStudyGuide] = useState(false);
  const [activeSermonId, setActiveSermonId] = useState(null);

  const openStudyGuide = (sermonId) => {
    setActiveSermonId(sermonId);
    setShowStudyGuide(true);
  };

  const closeStudyGuide = () => {
    setShowStudyGuide(false);
    setActiveSermonId(null);
  };

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="container mx-auto px-4 text-center">
          {/* Prophet Seal Logo */}
          <div className="mb-8 flex justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_author-books/artifacts/628p2jyw_Seal%20of%20the%20Prophet.jpg" 
              alt="Seal of the Prophet - Romero Q. Sumpter" 
              className="w-32 h-32 md:w-40 md:h-40 object-contain"
              style={{ 
                filter: 'drop-shadow(0 8px 32px rgba(255, 215, 0, 0.3))',
                mixBlendMode: 'multiply',
                backgroundColor: 'transparent'
              }}
            />
          </div>
          
          <h1 className="heading-1 mb-6">{ministry.title}</h1>
          <p className="body-large text-lime-400 mb-4">{ministry.subtitle}</p>
          <p className="body-medium text-gray-300 max-w-2xl mx-auto">
            {ministry.description}
          </p>
        </div>
      </section>

      {/* Special Announcement Banner */}
      <section className="py-16 bg-gradient-to-r from-purple-900 via-blue-800 to-purple-900 border-b-4 border-yellow-400">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-1/2 flex justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_author-books/artifacts/13lzt0z2_Appointment%20to%20the%20office%20of%20Overseer.jpg" 
                alt="Overseer Romero Q. Sumpter - Encounter Kingdom Glory Network" 
                className="w-full max-w-lg rounded-xl shadow-2xl hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="lg:w-1/2 text-center lg:text-left">
              <div className="mb-6">
                <span className="bg-yellow-400 text-black px-6 py-3 rounded-full font-bold text-lg uppercase tracking-wider shadow-lg">
                  🎉 Special Announcement
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 text-white uppercase leading-tight">
                Appointed as Overseer
              </h2>
              <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
                Honored to announce the appointment to the office of <strong className="text-yellow-400 text-2xl">Overseer</strong> with <strong className="text-yellow-400 text-2xl">Encounter Kingdom Glory Network</strong>. 
                Continuing the mission to inspire, create, and build for God's Kingdom.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center lg:justify-start">
                <a 
                  href="mailto:romero.sumpter@gmail.com?subject=Congratulations on Overseer Appointment"
                  className="btn-primary text-lg px-8 py-4"
                >
                  Send Congratulations
                </a>
                <a 
                  href="mailto:romero.sumpter@gmail.com?subject=Ministry Inquiry"
                  className="btn-secondary text-lg px-8 py-4"
                >
                  Ministry Inquiry
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="py-8 bg-gray-900 sticky top-20 z-40">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => setActiveTab('messages')}
              className={`px-6 py-3 rounded-full font-semibold uppercase tracking-wider text-sm transition-all ${
                activeTab === 'messages' 
                  ? 'bg-lime-400 text-black' 
                  : 'bg-transparent text-white border border-gray-600 hover:border-lime-400 hover:text-lime-400'
              }`}
            >
              Messages
            </button>
            <button
              onClick={() => setActiveTab('videos')}
              className={`px-6 py-3 rounded-full font-semibold uppercase tracking-wider text-sm transition-all ${
                activeTab === 'videos' 
                  ? 'bg-lime-400 text-black' 
                  : 'bg-transparent text-white border border-gray-600 hover:border-lime-400 hover:text-lime-400'
              }`}
            >
              Videos
            </button>
            <button
              onClick={() => setActiveTab('events')}
              className={`px-6 py-3 rounded-full font-semibold uppercase tracking-wider text-sm transition-all ${
                activeTab === 'events' 
                  ? 'bg-lime-400 text-black' 
                  : 'bg-transparent text-white border border-gray-600 hover:border-lime-400 hover:text-lime-400'
              }`}
            >
              Events
            </button>
            <button
              onClick={() => setActiveTab('podcasts')}
              className={`px-6 py-3 rounded-full font-semibold uppercase tracking-wider text-sm transition-all ${
                activeTab === 'podcasts' 
                  ? 'bg-lime-400 text-black' 
                  : 'bg-transparent text-white border border-gray-600 hover:border-lime-400 hover:text-lime-400'
              }`}
            >
              Podcasts
            </button>
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {/* Messages Section */}
          {activeTab === 'messages' && (
            <div>
              <h2 className="heading-2 mb-12 text-center">Sermon Messages</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                {ministry.messages.map((message) => (
                  <div key={message.id} className="service-card">
                    {/* Sermon Video or Image */}
                    {message.videoUrl ? (
                      <div className="mb-6 overflow-hidden rounded-lg bg-black">
                        <video 
                          src={message.videoUrl} 
                          controls
                          className="w-full aspect-video object-contain bg-black"
                          poster={message.image}
                          preload="metadata"
                        >
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    ) : message.image && (
                      <div className="mb-6 overflow-hidden rounded-lg bg-gray-800">
                        <img 
                          src={message.image} 
                          alt={message.title}
                          className="w-full h-80 object-contain bg-gray-800 hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    
                    <div className="flex items-start justify-between mb-4">
                      {message.duration && (
                        <div className="flex items-center text-gray-400">
                          <Clock size={14} className="mr-1" />
                          <span className="text-sm">{message.duration}</span>
                        </div>
                      )}
                    </div>
                    
                    <h3 className="heading-3 mb-2">{message.title}</h3>
                    {message.scripture && (
                      <p className="caption text-lime-400 mb-3 tracking-widest uppercase">{message.scripture}</p>
                    )}
                    <p className="body-small text-gray-300 mb-6">
                      {message.description}
                    </p>
                    
                    <div className="flex flex-col gap-3">
                      {message.hasStudyGuide && (
                        <button 
                          onClick={() => openStudyGuide(message.id)}
                          className="btn-primary w-full"
                        >
                          <BookOpen size={16} className="mr-2" />
                          Interactive Study Guide
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Videos Section */}
          {activeTab === 'videos' && (
            <div>
              <h2 className="heading-2 mb-12 text-center">Ministry Videos</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {ministry.videos.map((video) => (
                  <div key={video.id} className="service-card">
                    <div className="relative mb-4 overflow-hidden rounded-lg bg-black">
                      <video 
                        src={video.videoUrl} 
                        controls
                        className="w-full aspect-video object-contain bg-black"
                        poster={video.thumbnail}
                        preload="metadata"
                      >
                        Your browser does not support the video tag.
                      </video>
                    </div>
                    <div className="flex items-start justify-between mb-3">
                      {video.date && (
                        <span className="caption text-yellow-400">
                          {new Date(video.date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <h3 className="heading-3 mb-4">{video.title}</h3>
                    <p className="body-small text-gray-300 mb-6">
                      {video.id === 1 ? "A powerful prophetic message of release and breakthrough for this season." : 
                       video.id === 2 ? "An uplifting message of hope and encouragement during challenging times." : 
                       "A powerful ministry message."}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Events Section */}
          {activeTab === 'events' && (
            <div>
              <h2 className="heading-2 mb-12 text-center">Upcoming Events</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {ministry.events.map((event) => (
                  <div key={event.id} className="service-card">
                    {/* Event Image/Flyer */}
                    {event.image && (
                      <div className="mb-6 overflow-hidden rounded-lg bg-gray-800">
                        <img 
                          src={event.image} 
                          alt={event.title}
                          className="w-full h-80 object-contain bg-gray-800 hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center text-yellow-400">
                        <Calendar size={16} className="mr-2" />
                        <span className="caption">
                          {new Date(event.date).toLocaleDateString()}
                        </span>
                      </div>
                      <span className="caption text-gray-400">{event.time}</span>
                    </div>
                    
                    <h3 className="heading-3 mb-3">{event.title}</h3>
                    
                    <div className="flex items-center text-gray-400 mb-4">
                      <MapPin size={14} className="mr-2 flex-shrink-0" />
                      <span className="body-small">{event.location}</span>
                    </div>
                    
                    <p className="body-small text-gray-300 mb-6">
                      {event.description}
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button className="btn-primary flex-1">
                        <Users size={16} className="mr-2" />
                        More Info
                      </button>
                      <button className="btn-secondary">
                        <ExternalLink size={16} className="mr-2" />
                        Share Event
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Podcasts Section */}
          {activeTab === 'podcasts' && (
            <div>
              <h2 className="heading-2 mb-12 text-center">Ministry Podcasts</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {ministry.podcasts.map((podcast) => (
                  <div key={podcast.id} className="service-card">
                    {/* Podcast Image/Flyer */}
                    {podcast.image && (
                      <div className="mb-6 overflow-hidden rounded-lg">
                        <img 
                          src={podcast.image} 
                          alt={podcast.title}
                          className="w-full h-64 object-contain bg-gray-800 hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    
                    <div className="flex items-start justify-between mb-4">
                      <span className={`caption ${podcast.status === 'upcoming' ? 'text-yellow-400' : 'text-yellow-400'}`}>
                        {podcast.status === 'upcoming' ? 'Coming Soon' : 
                         podcast.id === 1 ? '2/1/2026' : 
                         podcast.id === 2 ? '2/8/2026' : 
                         new Date(podcast.date).toLocaleDateString()}
                      </span>
                      <div className="flex items-center text-gray-400">
                        <Clock size={14} className="mr-1" />
                        <span className="text-sm">{podcast.duration}</span>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <span className="caption text-gray-400 uppercase tracking-wider">
                        {podcast.series}
                      </span>
                    </div>
                    
                    <h3 className="heading-3 mb-2">{podcast.title}</h3>
                    {podcast.subtitle && (
                      <p className="body-small text-yellow-400 mb-3 font-semibold">
                        {podcast.subtitle}
                      </p>
                    )}
                    
                    <p className="body-small text-gray-300 mb-6">
                      {podcast.description}
                    </p>
                    
                    <div className="flex flex-col gap-3">
                      {podcast.status === 'upcoming' ? (
                        <button className="btn-secondary w-full opacity-50 cursor-not-allowed">
                          <Calendar size={16} className="mr-2" />
                          Coming February 2026
                        </button>
                      ) : (
                        <div className="space-y-3">
                          {podcast.audioUrl && podcast.audioUrl !== '#' && (
                            <div className="bg-gray-800 p-4 rounded-lg">
                              <audio 
                                controls 
                                className="w-full"
                                preload="metadata"
                              >
                                <source src={podcast.audioUrl} type="audio/wav" />
                                <source src={podcast.audioUrl} type="audio/mpeg" />
                                Your browser does not support the audio element.
                              </audio>
                            </div>
                          )}
                          <button className="btn-primary w-full">
                            <Play size={16} className="mr-2" />
                            Listen Now
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Podcast Info */}
              <div className="mt-16 text-center">
                <div className="max-w-2xl mx-auto bg-gray-800 p-8 rounded-lg">
                  <h3 className="heading-3 mb-4">Ministry Podcasts</h3>
                  <p className="body-medium text-gray-300 mb-6">
                    Join Prophet Romero Sumpter Sr. for powerful biblical teachings and insights. 
                    New episodes will be added regularly to strengthen your faith and deepen your understanding of God&apos;s Word.
                  </p>
                  <a 
                    href="mailto:romero.sumpter@gmail.com?subject=Podcast Suggestion"
                    className="btn-secondary"
                  >
                    Suggest a Topic
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 text-center">
          <h2 className="heading-2 mb-6">Join Our Ministry</h2>
          <p className="body-large text-gray-300 mb-8 max-w-2xl mx-auto">
            Stay connected with our ministry and never miss a message, video, or event that could transform your life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="btn-primary">
              Subscribe to Updates
            </button>
            <button className="btn-secondary">
              Follow on Social Media
            </button>
          </div>
        </div>
      </section>
      
      {/* Study Guide Modal */}
      {showStudyGuide && (
        <StudyGuide 
          sermonId={activeSermonId} 
          onClose={closeStudyGuide} 
        />
      )}
    </div>
  );
};

export default MinistryPage;